import { Component, effect, ElementRef, input, model, output, viewChild } from '@angular/core';

/**
 * Caixa de escrever do chat (estilo Claude): textarea que cresce com o texto em cima,
 * barra de ações embaixo. Enter envia, Shift+Enter quebra a linha.
 *
 * Sem i18n interno: textos (placeholder, dica, rótulo do enviar) entram por input.
 * Ações extras à esquerda da barra (anexar, por exemplo) entram projetadas com o
 * atributo `raChatComposerStart`.
 */
@Component({
    selector: 'ra-chat-composer',
    templateUrl: 'ra-chat-composer.component.html',
    styleUrl: 'ra-chat-composer.component.scss',
    host: { '[class.disabled]': 'disabled()' },
})
export class RaChatComposerComponent {
    /** Texto do rascunho (two-way: `[(value)]`). */
    public value = model('');
    public disabled = input(false);
    public placeholder = input('');
    /** Dica ao lado do enviar (ex.: "Enter para enviar"). */
    public hint = input('');
    /** Rótulo acessível do botão de enviar. */
    public sendLabel = input('');
    /**
     * Permite enviar sem texto (ex.: só com anexos). Quem decide se há o que mandar é o
     * chamador, por este input.
     */
    public allowEmpty = input(false);
    /**
     * O chamador pode segurar o envio sem travar a digitação (ex.: anexo ainda subindo).
     * Enquanto falso, Enter e o botão não enviam e o texto fica na caixa.
     */
    public canSend = input(true);
    /** Pedido de envio, com o texto já sem espaços nas pontas. */
    public send = output<string>();
    /**
     * Há algo em andamento que dá para parar: o enviar vira PARAR (e continua clicável,
     * mesmo com a caixa travada).
     */
    public stoppable = input(false);
    /** Parada já pedida, esperando confirmar: o botão fica apagado. */
    public stopping = input(false);
    /** Rótulo acessível do botão de parar. */
    public stopLabel = input('');
    public stop = output<void>();

    private _textarea = viewChild<ElementRef<HTMLTextAreaElement>>('textarea');

    constructor() {
        // O DOM segue o `value` por aqui, e não por um binding `[value]`: o binding compara
        // com o último valor que ELE escreveu, então um rascunho que entra e sai antes de
        // um ciclo de detecção (colar + Enter) ia de '' para '' e nunca saía da caixa.
        effect(() => {
            const value = this.value();
            const element = this._textarea()?.nativeElement;
            if (!element) return;

            if (element.value !== value) element.value = value;
            // Rascunho limpo (enviou ou o chamador zerou): volta à altura de uma linha.
            if (!value) element.style.height = '';
        });
    }

    public focus(): void {
        this._textarea()?.nativeElement.focus();
    }

    public onInput(event: Event): void {
        const element = event.target as HTMLTextAreaElement;
        this.value.set(element.value);
        this._autoGrow(element);
    }

    public onKeydown(event: KeyboardEvent): void {
        if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return;
        event.preventDefault();
        this.submit();
    }

    public submit(): void {
        const text = this.value().trim();
        if ((!text && !this.allowEmpty()) || this.disabled() || !this.canSend()) return;
        this.send.emit(text);

        // O composer se limpa sozinho, pelo mesmo motivo do effect acima: o chamador zerar o
        // rascunho dele não chega aqui quando o pai também não viu o texto passar.
        this.value.set('');
    }

    /** Cresce com o conteúdo (o teto vem do max-height no CSS). */
    private _autoGrow(element: HTMLTextAreaElement): void {
        element.style.height = 'auto';
        element.style.height = `${element.scrollHeight}px`;
    }
}
